import { gql } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import images from 'src/common/images';
import {
  ErrorPrimaryStore,
  ErrorSecondaryStore,
  MutationSet,
  PrimaryStore,
  SecondaryStore,
  Store,
} from 'src/common/interfaces';
import { RecordState } from 'src/common/string-enum';
import { InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import {
  deleteMstPrimarySales,
  deleteMstSecondarySales,
  insertMstPrimarySales,
  insertMstSecondarySales,
  updateMstPrimarySales,
  updateMstSecondarySales,
} from 'src/graphql/mutations';
import { getMstPrimarySales, getMstSecondarySales } from 'src/graphql/queries';
import { useAppLazyQuery, useAppMutation } from 'src/libs/hooks';
import utility from 'src/libs/utility';
import MainLayout from '../main-layout';
const defaultErrorPrimaryStore = {
  isErrorPrimaryCd: false,
  isErrorPrimaryNm: false,
};

const emptyPrimaryStore: PrimaryStore = {
  mps_primary_cd: '',
  mps_primary_nm: '',
  recordState: RecordState.INSERT,
  error: { ...defaultErrorPrimaryStore },
};

const emptySecondaryStore: SecondaryStore = {
  mss_primary_cd: '',
  mss_secondary_cd: '',
  mss_secondary_nm: '',
  recordState: RecordState.INSERT,
};

const MstSaleFlow = () => {
  const { confirmAlert, showAlertMessage, registerData } = utility;
  const header = ['コード', '名称', '追加', '削除'];
  const [mstPrimarySales, setMstPrimarySales] = useState<Array<PrimaryStore>>([]);
  const [mstSecondarySales, setMstSecondarySales] = useState<Array<SecondaryStore>>([]);
  const [mstStore, setMstStore] = useState<Array<Store>>([]);
  const [removedMstPrimaryStore, setRemovedMstPrimaryStore] = useState<Array<PrimaryStore>>([]);
  const [removedMstSecondaryStore, setRemovedMstSecondaryStore] = useState<Array<SecondaryStore>>([]);
  const { signOut } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const location = useLocation();
  const [insertPrimaryStoreFunc] = useAppMutation({ query: gql(insertMstPrimarySales) });
  const [updatePrimaryStoreFunc] = useAppMutation({ query: gql(updateMstPrimarySales) });
  const [deletePrimaryStoreFunc] = useAppMutation({ query: gql(deleteMstPrimarySales) });
  const [insertSecondaryStoreFunc] = useAppMutation({ query: gql(insertMstSecondarySales) });
  const [updateSecondaryStoreFunc] = useAppMutation({ query: gql(updateMstSecondarySales) });
  const [deleteSecondaryStoreFunc] = useAppMutation({ query: gql(deleteMstSecondarySales) });

  const [searchMstPrimarySales, { data: mstPrimarySalesData, loading: isMstPrimarySalesLoading }] = useAppLazyQuery({
    query: gql(getMstPrimarySales),
    onCompleted: () => {
      setRemovedMstPrimaryStore([]);
    },
  });
  const [searchMstSecondarySales, { data: mstSecondarySalesData, loading: isMstSecondarySalesLoading }] =
    useAppLazyQuery({
      query: gql(getMstSecondarySales),
      onCompleted: () => {
        setRemovedMstSecondaryStore([]);
      },
    });

  const preparePrimaryStoreDataApplyApi = (primaryStore: PrimaryStore) => {
    return {
      mps_primary_cd: parseInt(primaryStore?.mps_primary_cd),
      mps_primary_nm: primaryStore?.mps_primary_nm,
    };
  };

  const prepareSecondaryStoreDataApplyApi = (secondaryStore: SecondaryStore) => {
    return {
      mss_primary_cd: parseInt(secondaryStore?.mss_primary_cd),
      mss_secondary_cd: parseInt(secondaryStore?.mss_secondary_cd),
      mss_secondary_nm: secondaryStore?.mss_secondary_nm,
    };
  };

  const isExistPrimaryStore = () => {
    const newStoreList = [...mstStore];
    newStoreList.pop();
    const valueArr = newStoreList.map(function (store) {
      return store?.primary_store?.mps_primary_cd?.toString();
    });
    const isDuplicate = valueArr.some(function (item, idx) {
      if (valueArr.indexOf(item) !== idx && (item !== '' || item !== undefined)) {
        newStoreList[idx].primary_store.error.isErrorPrimaryCd = true;
        return true;
      }
      return false;
    });
    setMstStore(newStoreList);
    return isDuplicate;
  };

  const isExistSecondaryStore = () => {
    const newStoreList: any = [...mstStore];

    let isDuplicate = false;
    newStoreList?.map((store: Store, index: number) => {
      const filledScdStore = store?.secondary_store?.filter((scdStore) => {
        return !(
          scdStore.recordState === RecordState.INSERT &&
          (scdStore.mss_secondary_cd === undefined || scdStore?.mss_secondary_cd === '') &&
          scdStore?.mss_secondary_nm === ''
        );
      });

      const valueArr = filledScdStore?.map((scdStore) => {
        return scdStore?.mss_secondary_cd?.toString();
      });

      const isScdStore = valueArr?.some(function (item, idx) {
        if (valueArr.indexOf(item) !== idx) {
          newStoreList[index].secondary_store[idx].error.isErrorSecondaryCd = true;
          return true;
        }
        return false;
      });

      if (isScdStore === true) {
        isDuplicate = true;
      }
      return store;
    });

    setMstStore(newStoreList);
    return isDuplicate;
  };

  const checkErrorPrimaryStore = (primaryStore: PrimaryStore) => {
    if (
      (primaryStore?.mps_primary_cd === undefined || primaryStore?.mps_primary_cd === '') &&
      primaryStore?.mps_primary_nm === ''
    ) {
      return;
    } else {
      const isErrorPrimaryCd = primaryStore?.mps_primary_cd === undefined || primaryStore?.mps_primary_cd === '';
      const isErrorPrimaryNm = primaryStore?.mps_primary_nm === '';
      return { isErrorPrimaryCd, isErrorPrimaryNm };
    }
  };

  const checkErrorSecondaryStore = (secondaryStore: SecondaryStore) => {
    if (
      (secondaryStore?.mss_secondary_cd === undefined || secondaryStore?.mss_secondary_cd === '') &&
      secondaryStore?.mss_secondary_nm === ''
    ) {
      return;
    } else {
      const isErrorPrimaryCd = secondaryStore?.mss_primary_cd === undefined || secondaryStore?.mss_primary_cd === '';
      const isErrorSecondaryCd =
        secondaryStore?.mss_secondary_cd === undefined || secondaryStore?.mss_secondary_cd === '';
      const isErrorSecondaryNm = secondaryStore?.mss_secondary_nm === '';

      return { isErrorPrimaryCd, isErrorSecondaryCd, isErrorSecondaryNm };
    }
  };

  const validates = () => {
    const newStoreList = [...mstStore];
    let isSecondaryStoreInputError = false;
    const newListAddError: any = newStoreList?.map((store: Store, index) => {
      if (newStoreList?.length === index + 1) {
        return store;
      } else {
        let newSecondaryStore = store?.secondary_store;

        if (store?.secondary_store?.length > 0) {
          newSecondaryStore = store?.secondary_store?.map((scdStore) => {
            return {
              ...scdStore,
              error: checkErrorSecondaryStore(scdStore),
            };
          });
        }
        return {
          ...store,
          primary_store: {
            ...store?.primary_store,
            error: checkErrorPrimaryStore(store?.primary_store),
          },
          secondary_store: [...newSecondaryStore],
        };
      }
    });
    setMstStore(newListAddError);

    const filledPmrStore = newStoreList.filter((store) => {
      return !(
        (store?.primary_store?.mps_primary_cd === undefined || store?.primary_store?.mps_primary_cd === '') &&
        store?.primary_store?.mps_primary_nm === '' &&
        store?.primary_store?.recordState === RecordState.INSERT
      );
    });

    const indexItemPrimaryError = filledPmrStore?.findIndex(
      (store: Store) =>
        store?.primary_store?.mps_primary_cd === undefined ||
        store?.primary_store?.mps_primary_cd === '' ||
        store?.primary_store?.mps_primary_nm === ''
    );

    newStoreList?.map((store) => {
      const filledScdStore = store?.secondary_store?.filter((scdStore) => {
        return !(
          scdStore.recordState === RecordState.INSERT &&
          (scdStore.mss_secondary_cd === undefined || scdStore?.mss_secondary_cd === '') &&
          scdStore?.mss_secondary_nm === ''
        );
      });

      const index = filledScdStore?.findIndex(
        (secondaryStore: SecondaryStore) =>
          secondaryStore?.mss_secondary_cd === undefined ||
          secondaryStore?.mss_secondary_cd === '' ||
          secondaryStore?.mss_secondary_nm === ''
      );

      if (index !== -1) {
        isSecondaryStoreInputError = true;
      }

      return store;
    });

    if (indexItemPrimaryError !== -1 || isSecondaryStoreInputError) {
      showAlertMessage('', InfoMessage.CHECK_REQUIRED, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    if (isExistPrimaryStore() || isExistSecondaryStore()) {
      showAlertMessage('', InfoMessage.REGISTR_DUPLICATION, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    return true;
  };

  const renderTableData = useMemo(() => {
    if (mstStore?.length <= 0 || !mstStore) return;
    return mstStore?.map((store: Store, index: number) => {
      const styleInput = (isError: boolean | undefined) => {
        return `${
          isError ? 'border-[#FF0000] border-[2px] rounded-md' : 'border-{transparent}'
        } pl-[5px] bg-transparent h-[40px] w-full`;
      };

      return (
        <React.Fragment key={`primary_store_${index}`}>
          <tr className="bg-[#ffcccc]">
            <td className="p-[8px] border-[1px] border-solid border-[#fff]">
              <input
                className={styleInput(store?.primary_store?.error?.isErrorPrimaryCd)}
                onChange={(event) => {
                  if (event.target.value.length === 9) return false;
                  handleChangePrimaryStore(event?.target?.value, index, 'mps_primary_cd');
                }}
                type="number"
                name="mps_primary_cd"
                value={store?.primary_store?.mps_primary_cd?.toString()}
                disabled={
                  store?.primary_store?.recordState === undefined ||
                  store?.primary_store?.recordState === RecordState.NONE ||
                  store?.primary_store?.recordState === RecordState.UPDATE
                }
                min={1}
              />
            </td>
            <td className="p-[8px] border-[1px] border-solid border-[#fff]">
              <input
                className={styleInput(store?.primary_store?.error?.isErrorPrimaryNm)}
                onChange={(event) => {
                  if (event.target.value.length > 100) return false;
                  handleChangePrimaryStore(event?.target?.value, index, 'mps_primary_nm');
                }}
                name="mps_primary_nm"
                value={store?.primary_store?.mps_primary_nm}
                min={1}
              />
            </td>
            <td className="p-[8px] border-[1px] border-solid border-[#fff]">
              <button onClick={() => handleAddNewSecondaryStore(store?.primary_store?.mps_primary_cd, index)}>
                <img src={images.icPlus} alt="" className="w-[20px]" />
              </button>
            </td>
            <td className="p-[8px] border-[1px] border-solid border-[#fff]">
              <button onClick={() => onPressRemovePrimaryStore(index)}>
                <img src={images.icDelete} alt="" className="w-[20px]" />
              </button>
            </td>
          </tr>
          {store?.secondary_store?.length > 0 &&
            store?.secondary_store?.map((secondaryStore, secondIndex) => {
              return (
                <tr className="bg-[#f5f5f5]" key={`secondary_store_${secondaryStore?.mss_primary_cd}_${secondIndex}`}>
                  <td className="p-[8px] border-[1px] border-solid border-[#fff]">
                    <input
                      className={styleInput(secondaryStore?.error?.isErrorSecondaryCd)}
                      onChange={(event) => {
                        if (event.target.value.length === 9) return false;
                        handleChangeSecondaryStore(event?.target?.value, index, secondIndex, 'mss_secondary_cd');
                      }}
                      type="number"
                      name="mss_secondary_cd"
                      value={secondaryStore?.mss_secondary_cd?.toString()}
                      disabled={
                        secondaryStore?.recordState === undefined ||
                        secondaryStore?.recordState === RecordState.NONE ||
                        secondaryStore?.recordState === RecordState.UPDATE
                      }
                      min={1}
                    />
                  </td>
                  <td className="p-[8px] border-[1px] border-solid border-[#fff]">
                    <input
                      className={styleInput(secondaryStore?.error?.isErrorSecondaryNm)}
                      onChange={(event) => {
                        if (event.target.value.length > 100) return false;
                        handleChangeSecondaryStore(event?.target?.value, index, secondIndex, 'mss_secondary_nm');
                      }}
                      name="mss_secondary_nm"
                      value={secondaryStore?.mss_secondary_nm}
                      min={1}
                    />
                  </td>
                  <td className="p-[8px] border-[1px] border-solid border-[#fff]"></td>
                  <td className="p-[8px] border-[1px] border-solid border-[#fff]">
                    <button onClick={() => onPressRemoveSecondaryStore(index, secondIndex)}>
                      <img src={images.icDelete} alt="" className="w-[20px]" />
                    </button>
                  </td>
                </tr>
              );
            })}
        </React.Fragment>
      );
    });
  }, [mstStore]);

  const handleChangePrimaryStore = (value: string, index: number, typeStore: string) => {
    const newStoreList = [...mstStore];
    if (
      newStoreList[index].primary_store?.recordState === undefined ||
      newStoreList[index].primary_store?.recordState === RecordState.NONE
    ) {
      newStoreList[index].primary_store.recordState = RecordState.UPDATE;
    }
    const errorObject: ErrorPrimaryStore = { ...newStoreList[index]?.primary_store?.error };
    switch (typeStore) {
      case 'mps_primary_cd':
        errorObject.isErrorPrimaryCd = false;
        newStoreList[index].primary_store.mps_primary_cd = value;
        newStoreList[index]?.secondary_store?.forEach((secondaryStore) => {
          secondaryStore.mss_primary_cd = value;
        });
        break;
      case 'mps_primary_nm':
        errorObject.isErrorPrimaryNm = false;
        newStoreList[index].primary_store.mps_primary_nm = value;
        break;
    }
    newStoreList[index].primary_store.error = { ...newStoreList[index]?.primary_store?.error, ...errorObject };

    setMstStore(newStoreList);
  };

  const handleChangeSecondaryStore = (value: string, index: number, secondIndex: number, typeStore: string) => {
    const newStoreList: any = [...mstStore];
    const secondaryStore = [...newStoreList[index].secondary_store];
    if (
      secondaryStore[secondIndex]?.recordState === RecordState.NONE ||
      secondaryStore[secondIndex]?.recordState === undefined
    ) {
      secondaryStore[secondIndex].recordState = RecordState.UPDATE;
    }
    const errorObject: ErrorSecondaryStore = { ...secondaryStore[secondIndex]?.error };
    switch (typeStore) {
      case 'mss_primary_cd':
        errorObject.isErrorPrimaryCd = false;
        break;
      case 'mss_secondary_cd':
        errorObject.isErrorSecondaryCd = false;
        secondaryStore[secondIndex].mss_secondary_cd = value;
        break;
      case 'mss_secondary_nm':
        errorObject.isErrorSecondaryNm = false;
        secondaryStore[secondIndex].mss_secondary_nm = value;
        break;
    }
    secondaryStore[secondIndex].error = { ...secondaryStore[secondIndex].error, ...errorObject };
    setMstStore(newStoreList);
  };

  const handleAddNewSecondaryStore = (primaryStoreCd: string, index: number) => {
    const newStoreList = [...mstStore];
    const primaryStore = newStoreList[index].primary_store;
    if (primaryStore?.mps_primary_cd === undefined || primaryStore?.mps_primary_cd === '') {
      newStoreList[index].primary_store.error.isErrorPrimaryCd = true;
      showAlertMessage('', InfoMessage.CHECK_CODE_REQUIRED, InfoWord.ERROR, InfoWord.OK);
    } else {
      newStoreList[index].secondary_store?.push({ ...emptySecondaryStore, mss_primary_cd: primaryStoreCd });
    }

    setMstStore(newStoreList);
  };

  const renderTableHeader = () => {
    return header.map((key, index) => (
      <th
        className="p-[8px] border-[1px] border-solid border-[#fff] pt-[12px] pb-[12px] text-center bg-[#a8d08d] text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  };

  const onPressRemovePrimaryStore = (index: number) => {
    confirmAlert(InfoMessage.CONFIRM_REMOVE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () => {
      const newStoreList = [...mstStore];
      const newDeletePrimaryStore = [...removedMstPrimaryStore];
      const newDeleteSecondaryStore = [...removedMstSecondaryStore];

      const deletePrimaryStore = newStoreList.splice(index, 1);
      if (deletePrimaryStore[0]?.primary_store?.recordState !== RecordState.INSERT) {
        newDeletePrimaryStore?.push({
          ...deletePrimaryStore[0]?.primary_store,
          recordState: RecordState.DELETE,
        });

        if (deletePrimaryStore[0]?.secondary_store?.length > 0) {
          deletePrimaryStore[0]?.secondary_store?.map((secondaryStore) => {
            if (secondaryStore?.recordState !== RecordState.INSERT) {
              newDeleteSecondaryStore?.push({
                ...secondaryStore,
                recordState: RecordState.DELETE,
              });
            }
            return secondaryStore;
          });
        }
      }

      setRemovedMstPrimaryStore(newDeletePrimaryStore);
      setRemovedMstSecondaryStore(newDeleteSecondaryStore);
      setMstStore(newStoreList);
    });
  };

  const onPressRemoveSecondaryStore = (index: number, secondIndex: number) => {
    confirmAlert(InfoMessage.CONFIRM_REMOVE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () => {
      const newStoreList = [...mstStore];
      const secondaryStore: SecondaryStore[] = [...mstStore[index].secondary_store];
      const newDeleteSecondaryStore = [...removedMstSecondaryStore];
      const removeStore = secondaryStore?.splice(secondIndex, 1);

      if (removeStore[0]?.recordState !== RecordState.INSERT) {
        newDeleteSecondaryStore?.push({
          ...removeStore[0],
          recordState: RecordState.DELETE,
        });
      }

      newStoreList[index].secondary_store = secondaryStore;

      setRemovedMstSecondaryStore(newDeleteSecondaryStore);
      setMstStore(newStoreList);
    });
  };

  const handleSearch = async () => {
    await searchMstPrimarySales();
    await searchMstSecondarySales();
  };

  const onPressConfirmRegister = () => {
    Auth.currentAuthenticatedUser()
      .then(() => {
        confirmAlert(InfoMessage.CONFIRM_REGISTR, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, onRegister);
      })
      .catch(() => {
        signOut();
        navigate('/login', { replace: true, state: { from: location } });
      });
  };

  const onRegister = async () => {
    const storeList = [...mstStore];
    storeList.pop();

    const updPStore: PrimaryStore[] = [];
    const insPStore: PrimaryStore[] = [];
    const updSStore: SecondaryStore[] = [];
    const insSStore: SecondaryStore[] = [];

    // 登録数もさほど多くならないので、総当たりでチェックを行う
    storeList.forEach((store) => {
      switch (store.primary_store.recordState) {
        case RecordState.NONE:
          return;
        case RecordState.UPDATE:
          updPStore.push(store.primary_store);
          break;
        case RecordState.INSERT:
          insPStore.push(store.primary_store);
          break;
      }
      store.secondary_store.forEach((sStore) => {
        switch (sStore.recordState) {
          case RecordState.NONE:
            return;
          case RecordState.UPDATE:
            updSStore.push(sStore);
            break;
          case RecordState.INSERT:
            insSStore.push(sStore);
            break;
        }
      });
    });

    const delSetP: MutationSet<PrimaryStore> = {
      recordState: RecordState.DELETE,
      data: removedMstPrimaryStore,
      func: (data: PrimaryStore) =>
        deletePrimaryStoreFunc({ variables: { input: { mps_primary_cd: data.mps_primary_cd } } }),
    };

    const updSetP: MutationSet<PrimaryStore> = {
      recordState: RecordState.UPDATE,
      data: updPStore,
      func: (data: PrimaryStore) =>
        updatePrimaryStoreFunc({ variables: { input: preparePrimaryStoreDataApplyApi(data) } }),
    };

    const insSetP: MutationSet<PrimaryStore> = {
      recordState: RecordState.INSERT,
      data: insPStore,
      func: (data: PrimaryStore) =>
        insertPrimaryStoreFunc({ variables: { input: preparePrimaryStoreDataApplyApi(data) } }),
    };

    const delSetS: MutationSet<SecondaryStore> = {
      recordState: RecordState.DELETE,
      data: removedMstSecondaryStore,
      func: (data: SecondaryStore) =>
        deleteSecondaryStoreFunc({
          variables: {
            input: { mss_primary_cd: data.mss_primary_cd, mss_secondary_cd: data.mss_secondary_cd },
          },
        }),
    };

    const updSetS: MutationSet<SecondaryStore> = {
      recordState: RecordState.UPDATE,
      data: updSStore,
      func: (data: SecondaryStore) =>
        updateSecondaryStoreFunc({ variables: { input: prepareSecondaryStoreDataApplyApi(data) } }),
    };

    const insSetS: MutationSet<SecondaryStore> = {
      recordState: RecordState.INSERT,
      data: insSStore,
      func: (data: SecondaryStore) =>
        insertSecondaryStoreFunc({ variables: { input: prepareSecondaryStoreDataApplyApi(data) } }),
    };

    registerData({
      mutationSets1: [delSetP, updSetP, insSetP],
      mutationSets2: [delSetS, updSetS, insSetS],
      validates,
      searchFunc: handleSearch,
    });
  };

  useEffect(() => {
    if (!mstPrimarySalesData) return;
    const newListMstPrimaryStore = { ...mstPrimarySalesData };
    const listPrimarySales: PrimaryStore[] =
      newListMstPrimaryStore?.getMstPrimarySales?.sort(
        (store1: any, store2: any) => store1.mps_primary_cd - store2.mps_primary_cd
      ) || [];

    setMstPrimarySales(listPrimarySales);
  }, [mstPrimarySalesData]);

  useEffect(() => {
    if (!mstSecondarySalesData) return;
    const newListMstPrimaryStore = { ...mstSecondarySalesData };
    const listPrimarySales: SecondaryStore[] =
      newListMstPrimaryStore?.getMstSecondarySales?.sort(
        (store1: any, store2: any) => store1?.mss_secondary_cd - store2?.mss_secondary_cd
      ) || [];

    setMstSecondarySales(listPrimarySales);
  }, [mstSecondarySalesData]);

  useEffect(() => {
    const newSecondaryStoreList = [...mstSecondarySales];

    const newStoreList: Store[] = mstPrimarySales?.map((mstPrimarySale: PrimaryStore) => {
      const secondaryStoreList = newSecondaryStoreList?.filter((mstSecondarySale: SecondaryStore) => {
        return mstSecondarySale.mss_primary_cd === mstPrimarySale?.mps_primary_cd;
      });

      return {
        primary_store: mstPrimarySale,
        secondary_store: [...secondaryStoreList],
      };
    });

    setMstStore(newStoreList);
  }, [mstPrimarySales, mstSecondarySales]);

  useEffect(() => {
    if (mstStore?.length < 1) return;
    const lastIndex = mstStore?.length - 1;
    const newListMstStore = [...mstStore];
    const lastItem = newListMstStore[lastIndex];
    const behindLastItem = newListMstStore[lastIndex - 1];
    const isLastItemFill =
      lastItem?.primary_store?.mps_primary_nm !== '' || lastItem?.primary_store?.mps_primary_cd !== '';
    const isBehindLastItemFill =
      behindLastItem?.primary_store?.mps_primary_nm === '' && behindLastItem?.primary_store?.mps_primary_cd === '';

    if (isLastItemFill) {
      newListMstStore?.push({
        primary_store: { ...emptyPrimaryStore, error: { ...defaultErrorPrimaryStore } },
        secondary_store: [],
      });
      setMstStore(newListMstStore);
    }
    if (isBehindLastItemFill) {
      newListMstStore?.pop();
      setMstStore(newListMstStore);
    }
  }, [mstStore]);

  useEffect(() => {
    handleSearch();
  }, []);

  return (
    <MainLayout>
      <div className="justify-center items-center">
        <div className="flex flex-col justify-center items-center">
          <p className="p-[20px]">商流管理</p>
          <div className="flex w-3/5">
            <button
              type="submit"
              onClick={onPressConfirmRegister}
              className="border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[100px] flex justify-center items-center"
            >
              <b>登録</b>
            </button>
          </div>

          <table className="text-center border-collapse border-[3px] border-solid border-[#ddd] w-3/5 td:p-[10px]">
            <tbody>
              <tr className="w-1/2">{renderTableHeader()}</tr>
              {isMstPrimarySalesLoading || isMstSecondarySalesLoading ? (
                <tr className="hover:opacity-80 even:bg-[#e2efd9] w-1/2">
                  <td
                    colSpan={header.length}
                    className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                renderTableData
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MainLayout>
  );
};

export default MstSaleFlow;
